import { FC, MouseEvent } from 'react'

import { Box, BoxProps, IconButton, IconButtonProps, Tooltip } from '@chakra-ui/react'

interface Props extends IconButtonProps {
    icon: JSX.Element
    view: 'primary' | 'secondary' | 'error' | 'success' | 'ghost'
    size: 'sm' | 'md' | 'lg' | 'xs'
    tooltip?: string
    tooltipProps?: BoxProps
    darkTooltip?: boolean
}

export const MainIconButton: FC<Props> = ({
    icon,
    view,
    size,
    tooltip,
    tooltipProps,
    darkTooltip = false,
    onMouseDown,
    ...props
}): JSX.Element | null => {
    const getSize = () => {
        switch (size) {
            case 'xs':
                return '24px'
            case 'sm':
                return '28px'
            case 'md':
                return '36px'
            case 'lg':
                return '44px'
            default:
                return '36px'
        }
    }
    const defaultProps = {
        variant: 'unstyled',
        minW: getSize(),
        h: getSize(),
        borderRadius: '6px',
        icon: icon,
        _focus: { boxShadow: '0 0 0 2px #245AF066' },
        _disabled: {
            opacity: 0.5,
            bg: '#F5F5F5',
            color: '#0C0C0D',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            svg: { color: '#0C0C0D' },
        },
    }

    const handleMouseDown = (event: MouseEvent<HTMLButtonElement>) => {
        event.preventDefault()
        onMouseDown?.(event)
    }

    const renderButton = (buttonProps: any) => {
        const button = <IconButton {...buttonProps} onMouseDown={handleMouseDown} />

        if (tooltip) {
            return (
                <Box {...tooltipProps}>
                    <Tooltip
                        label={tooltip}
                        hasArrow
                        openDelay={darkTooltip ? 500 : undefined}
                        px={darkTooltip ? undefined : '16px'}
                        py={darkTooltip ? undefined : '8px'}
                        padding={darkTooltip ? '8px 16px' : undefined}
                        borderRadius={darkTooltip ? '4px' : '6px'}
                        backgroundColor={darkTooltip ? '#000000' : undefined}
                        color={darkTooltip ? '#FFFFFF' : undefined}
                        fontWeight={darkTooltip ? 600 : undefined}
                        fontSize={darkTooltip ? '12px' : undefined}
                        placement="top"
                        closeOnClick
                        closeOnPointerDown
                        modifiers={[
                            {
                                name: 'preventOverflow',
                                options: { boundary: 'viewport', padding: 8 },
                            },
                        ]}
                        sx={
                            darkTooltip
                                ? {
                                      '--popper-arrow-bg': '#000000',
                                  }
                                : undefined
                        }
                    >
                        {button}
                    </Tooltip>
                </Box>
            )
        }

        return button
    }

    if (view === 'primary') {
        return renderButton({
            bg: '#245AF0',
            color: '#FFF',
            _hover: { bg: '#1D48C0' },
            _active: { bg: '#163690' },
            ...defaultProps,
            ...props,
        })
    } else if (view === 'secondary') {
        return renderButton({
            border: '1px solid #CBCBCD',
            bg: '#FFF',
            _hover: { bg: '#F5F5F5' },
            _active: { bg: '#E0E0E1' },
            ...defaultProps,
            ...props,
        })
    } else if (view === 'error') {
        return renderButton({
            bg: '#DB3333',
            color: '#FFF',
            _hover: { bg: '#B62626' },
            _active: { bg: '#881C1C' },
            ...defaultProps,
            ...props,
        })
    } else if (view === 'success') {
        return renderButton({
            bg: '#00853D',
            color: '#FFF',
            _hover: { bg: '#006D33' },
            _active: { bg: '#005226' },
            ...defaultProps,
            ...props,
        })
    } else if (view === 'ghost') {
        return renderButton({
            bg: 'transparent',
            border: 'none',
            _hover: { bg: '#EAEAEB' },
            _active: { bg: '#E0E0E1' },
            ...defaultProps,
            ...props,
        })
    } else {
        return null
    }
}
